import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Shield, AlertCircle, Send, Pencil, Link2, X, Check, Copy, AlertTriangle } from 'lucide-react';
import HubLayout from '../common/HubLayout';
import { getUserFamiliesDetailed, getFamilyMembers, axiosInstance } from '../../services/api';

function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

function getStatusInfo(member) {
  if (member.living === false) {
    return { color: 'bg-gray-400', label: 'Inactive' };
  }
  if (member.is_minor) {
    const guardianName = member.guardian_name || member.parent_name || 'Guardian';
    return { color: 'bg-blue-500', label: `Under Guardian (${guardianName})` };
  }
  if (member.has_account && member.invitation_accepted) {
    return { color: 'bg-green-500', label: 'Active Member' };
  }
  if (!member.invitation_accepted && (member.invitation_id || !member.has_account)) {
    return { color: 'bg-orange-500', label: 'Invitation Sent' };
  }
  return { color: 'bg-purple-400', label: 'Genealogy Only' };
}

function getRoleBadgeClasses(role) {
  switch (role) {
    case 'admin':
      return 'bg-purple-100 text-purple-800';
    case 'viewer':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

/** Returns true if this member can receive a resend or copy-link action */
function isPendingInvitation(member) {
  if (member.is_minor || member.living === false || member.invitation_accepted) return false;
  // Pending: either no account (standard invite) or has account but hasn't accepted (invitation_id present)
  return !member.has_account || !!member.invitation_id;
}

const FAMILY_COLORS = [
  'bg-[#556B2F]',
  'bg-[#8B4513]',
  'bg-[#4A6FA5]',
  'bg-[#7B2D8B]',
  'bg-[#B8860B]',
  'bg-[#2E8B57]',
];

function ResendConfirmModal({ isOpen, onClose, onConfirm, member, sending }) {
  if (!isOpen) return null;

  const memberName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-orange-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Resend Invitation</h3>
        </div>

        <p className="text-sm text-gray-600 mb-2">
          You are about to resend the invitation to <strong>{memberName}</strong>.
        </p>
        <p className="text-sm text-gray-600 mb-5">
          This action will <strong>void the previous invitation link</strong> and generate a new one. The old link will no longer work.
        </p>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-5">
          <p className="text-xs text-orange-800">
            By clicking Continue, you agree to void the last invitation link and send a new one to {memberName}.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-60"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={14} />
                Continue
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyLinkModal({ isOpen, onClose, familyId, member }) {
  const [invitationLink, setInvitationLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen || !member || !familyId) return;
    setLoading(true);
    setError(null);
    setCopied(false);
    setInvitationLink('');

    axiosInstance.get(`/families/${familyId}/members/${member.id}/invitation-link/`)
      .then(res => {
        if (res.data.success) {
          setInvitationLink(res.data.invitation_link);
        } else {
          setError(res.data.error || 'Could not retrieve invitation link.');
        }
      })
      .catch(err => {
        const msg = err.response?.data?.error || 'Failed to get invitation link.';
        const isExpired = err.response?.data?.expired;
        setError(isExpired ? 'No active invitation. Resend the invitation first to generate a new link.' : msg);
      })
      .finally(() => setLoading(false));
  }, [isOpen, familyId, member]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = invitationLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  const memberName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Copy Invitation Link</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Copy the invitation link for <strong>{memberName}</strong> to share via text, email, or messaging app.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-8 h-8 border-3 border-[#556B2F] border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {error}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <input
                type="text"
                readOnly
                value={invitationLink}
                className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
              />
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                  copied
                    ? 'bg-green-100 text-green-700'
                    : 'bg-[#556B2F] text-white hover:bg-[#4A5D23]'
                }`}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              This link allows {memberName} to accept the invitation and join the family.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function FamilyManager() {
  const navigate = useNavigate();
  const [adminFamilies, setAdminFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [resendModal, setResendModal] = useState({ open: false, familyId: null, member: null });
  const [resending, setResending] = useState(false);
  const [copyLinkModal, setCopyLinkModal] = useState({ open: false, familyId: null, member: null });
  const [selectedFamilyId, setSelectedFamilyId] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getUserFamiliesDetailed();
      const allFamilies = data.families || [];
      const adminOnly = allFamilies.filter(f => f.role === 'admin');

      if (adminOnly.length === 0) {
        setAdminFamilies([]);
        setLoading(false);
        return;
      }

      const results = await Promise.all(
        adminOnly.map(async (family) => {
          const membersData = await getFamilyMembers(family.id);
          const members = membersData.members || [];
          return { family, members };
        })
      );

      setAdminFamilies(results);
      setLoading(false);
    } catch (err) {
      console.error('Error loading family data:', err);
      setError('Failed to load family data. Please try again.');
      setLoading(false);
    }
  };

  const handleResendConfirm = async () => {
    const { familyId, member } = resendModal;
    if (!familyId || !member) return;

    const memberName = `${member.first_name || ''} ${member.last_name || ''}`.trim();
    setResending(true);
    try {
      const response = await axiosInstance.post(
        `/families/${familyId}/members/${member.id}/resend-invitation/`
      );
      if (response.data.success) {
        setResendModal({ open: false, familyId: null, member: null });
        alert(`New invitation sent to ${memberName}. The previous link has been voided.`);
      } else {
        throw new Error(response.data.error);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to resend invitation';
      alert(`Error: ${msg}`);
    } finally {
      setResending(false);
    }
  };

  const filterMembers = (members) => {
    if (!searchTerm) return members;
    const term = searchTerm.toLowerCase();
    return members.filter(m => {
      const name = `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase();
      return name.includes(term);
    });
  };

  const displayedFamilies = selectedFamilyId === 'all'
    ? adminFamilies
    : adminFamilies.filter(({ family }) => family.id === selectedFamilyId);

  const totalFilteredCount = displayedFamilies.reduce(
    (sum, { members }) => sum + filterMembers(members).length,
    0
  );

  return (
    <HubLayout activeView="members">
      <div className="p-6 sm:p-8 max-w-6xl">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Family Members Management</h1>
          <p className="text-gray-600 text-lg">
            Manage members across families where you are an admin.
          </p>
        </div>

        {/* Search Bar & Family Filter */}
        <div className="mb-8 flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search members by name..."
              className="w-full p-4 pl-12 rounded-full border-2 border-[#556B2F] text-lg focus:outline-none focus:ring-2 focus:ring-[#556B2F] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={22} />
          </div>

          {adminFamilies.length > 1 && (
            <select
              value={selectedFamilyId}
              onChange={(e) => setSelectedFamilyId(e.target.value)}
              className="px-4 py-4 rounded-full border-2 border-[#556B2F] text-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
            >
              <option value="all">All Families</option>
              {adminFamilies.map(({ family }) => (
                <option key={family.id} value={family.id}>{family.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin w-12 h-12 border-4 border-[#556B2F] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Loading family members...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-lg text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#556B2F] text-white px-6 py-2 rounded-full hover:bg-[#4A5D23] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : adminFamilies.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-xl text-gray-700 mb-2">You are not an admin of any families</p>
            <p className="text-gray-500">
              You need admin access to a family to manage its members.
            </p>
          </div>
        ) : (
          <>
            {searchTerm && (
              <p className="text-sm text-gray-500 mb-4">
                {totalFilteredCount} member{totalFilteredCount !== 1 ? 's' : ''} matching "{searchTerm}"
              </p>
            )}

            {displayedFamilies.map(({ family, members }, familyIndex) => {
              const filtered = filterMembers(members);
              if (searchTerm && filtered.length === 0) return null;

              const colorClass = FAMILY_COLORS[familyIndex % FAMILY_COLORS.length];

              return (
                <div key={family.id} className="mb-10">
                  {/* Family Section Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-10 h-10 rounded-full ${colorClass} text-white flex items-center justify-center font-bold text-lg`}>
                      {(family.name || 'F').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{family.name}</h2>
                      <p className="text-sm text-gray-500">
                        {filtered.length} member{filtered.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Table Header */}
                  <div className="bg-gray-100 px-6 py-3 rounded-t-lg grid grid-cols-[1fr_1fr_0.5fr_1fr_0.5fr_auto] gap-4 font-semibold text-sm text-gray-600 uppercase tracking-wide">
                    <div>Name</div>
                    <div>Relationship</div>
                    <div>Age</div>
                    <div>Status</div>
                    <div>Role</div>
                    <div className="w-48 text-center">Actions</div>
                  </div>

                  {/* Member Rows */}
                  <div className="border border-t-0 border-gray-200 rounded-b-lg divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500">
                        No members in this family.
                      </div>
                    ) : (
                      filtered.map((member) => {
                        const name = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown';
                        const age = calculateAge(member.date_of_birth);
                        const status = getStatusInfo(member);
                        const role = member.role || 'member';
                        const pending = isPendingInvitation(member);

                        return (
                          <div
                            key={member.id}
                            className="bg-white px-6 py-4 grid grid-cols-[1fr_1fr_0.5fr_1fr_0.5fr_auto] gap-4 items-center hover:bg-gray-50 transition-colors"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{name}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 capitalize">
                                {member.relationship || 'Family Member'}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-700">
                                {age !== null ? age : '\u2014'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${status.color} flex-shrink-0`}></div>
                              <span className="text-sm text-gray-600">{status.label}</span>
                            </div>
                            <div>
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeClasses(role)}`}>
                                {role}
                              </span>
                            </div>
                            {/* Actions */}
                            <div className="flex items-center gap-2 w-48 justify-center">
                              {/* Edit / View Profile */}
                              <button
                                onClick={() => navigate(`/family/${family.id}/members/${member.id}/edit`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                              >
                                <Pencil size={13} />
                                Edit
                              </button>

                              {/* Resend Invitation */}
                              {pending && (
                                <button
                                  onClick={() => setResendModal({ open: true, familyId: family.id, member })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                                >
                                  <Send size={13} />
                                  Resend
                                </button>
                              )}

                              {/* Copy Invitation Link */}
                              {pending && (
                                <button
                                  onClick={() => setCopyLinkModal({ open: true, familyId: family.id, member })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                >
                                  <Copy size={13} />
                                  Link
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Legend */}
        {!loading && !error && adminFamilies.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span> Active Member
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block"></span> Invitation Sent
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-400 inline-block"></span> Genealogy Only
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-400 inline-block"></span> Inactive
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span> Under Guardian
            </span>
          </div>
        )}
      </div>

      {/* Resend Confirmation Modal */}
      <ResendConfirmModal
        isOpen={resendModal.open}
        onClose={() => setResendModal({ open: false, familyId: null, member: null })}
        onConfirm={handleResendConfirm}
        member={resendModal.member}
        sending={resending}
      />

      {/* Copy Link Modal */}
      <CopyLinkModal
        isOpen={copyLinkModal.open}
        onClose={() => setCopyLinkModal({ open: false, familyId: null, member: null })}
        familyId={copyLinkModal.familyId}
        member={copyLinkModal.member}
      />
    </HubLayout>
  );
}

export default FamilyManager;
